import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { roomsApi, pathsApi, challengesApi } from '../services/api';
import { Trophy, Flame, Target, TrendingUp, Play, Loader2, Zap, CheckCircle2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import AuthModal from '../components/AuthModal';
import Footer from '../components/Footer';
import SandboxCreditsCard from '../components/SandboxCreditsCard';
import UpgradePrompt from '../components/UpgradePrompt';
import { SoftResubscribeBanner } from '../components/SoftResubscribeBanner';

// Difficulty → badge color classes. Replaces two nested ternary chains on
// lines 180 and 286 with a single lookup. Unknown difficulties fall back
// to the "Insane" purple so nothing renders unstyled.
const DIFFICULTY_BADGE = {
  Easy:   'border-[#00FF00] text-[#00FF00]',
  Medium: 'border-[#FF9500] text-[#FF9500]',
  Hard:   'border-[#FF0055] text-[#FF0055]',
};
const difficultyBadgeClass = (d) =>
  DIFFICULTY_BADGE[d] || 'border-[#9D00FF] text-[#9D00FF]';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [paths, setPaths] = useState([]);
  const [daily, setDaily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [roomsData, pathsData, dailyData] = await Promise.all([
          roomsApi.getAll(),
          pathsApi.getAll(),
          challengesApi.getDaily().catch(() => null),
        ]);
        setRooms(roomsData);
        setPaths(pathsData);
        setDaily(dailyData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Show auth prompt if not logged in
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      setShowAuthModal(true);
    }
  }, [authLoading, isAuthenticated]);

  const userStats = isAuthenticated && user ? {
    username: user.username || 'User',
    rank: user.rank || 0,
    points: user.points || 0,
    streak: user.streak || 0,
    roomsCompleted: user.completedRooms?.length || 0,
    totalRooms: rooms.length || 0,
  } : {
    username: 'Guest',
    rank: '-',
    points: 0,
    streak: 0,
    roomsCompleted: 0,
    totalRooms: rooms.length || 0,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00FF00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-16">
        {/* Soft win-back banner — hidden unless server says user is loyalty
            eligible (legacy one-shot Pro in renewal/win-back window). */}
        <SoftResubscribeBanner />

        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            Welcome back, {userStats.username}!
          </h1>
          <p className="text-gray-400">Continue your cybersecurity journey</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400">Rank</CardTitle>
                <Trophy className="w-4 h-4 text-[#FFD700]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">
                {typeof userStats.rank === 'number' ? `#${userStats.rank}` : userStats.rank}
              </div>
              <p className="text-xs text-gray-500 mt-1">Global Ranking</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400">Points</CardTitle>
                <Target className="w-4 h-4 text-[#00FF00]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{userStats.points}</div>
              <p className="text-xs text-gray-500 mt-1">Total Points Earned</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400">Streak</CardTitle>
                <Flame className="w-4 h-4 text-[#FF9500]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{userStats.streak} days</div>
              <p className="text-xs text-gray-500 mt-1">Current Streak</p>
            </CardContent>
          </Card>

          <Card className="bg-[#1A1A1A] border-gray-800">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-gray-400">Progress</CardTitle>
                <TrendingUp className="w-4 h-4 text-[#00BFFF]" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-white">{userStats.roomsCompleted}/{userStats.totalRooms}</div>
              <p className="text-xs text-gray-500 mt-1">Rooms Completed</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Daily Challenge */}
            {daily && daily.room && (
              <Card
                data-testid="daily-challenge-card"
                className="bg-gradient-to-br from-[#1A1A1A] to-[#0A1F0A] border-[#00FF00]/30 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-48 h-48 bg-[#00FF00]/5 rounded-full blur-3xl" />
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-[#00FF00]" />
                      <CardTitle className="text-white">Daily Challenge</CardTitle>
                      <Badge className="bg-[#00FF00]/15 text-[#00FF00] border-[#00FF00]/40">
                        {daily.bonus_multiplier}x POINTS
                      </Badge>
                    </div>
                    <span className="text-xs text-gray-500">{daily.date}</span>
                  </div>
                  <CardDescription className="text-gray-400">
                    {daily.completed_today
                      ? 'You already crushed today\u2019s challenge. Come back tomorrow.'
                      : `Complete this room today for ${daily.bonus_multiplier}x points.`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border border-gray-800 rounded-lg p-4 bg-[#0A0A0A]/50 relative z-10">
                    <div className="flex items-start justify-between mb-3 gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-white">{daily.room.title}</h3>
                          <Badge
                            variant="outline"
                            className={`text-xs ${difficultyBadgeClass(daily.room.difficulty)}`}
                          >
                            {daily.room.difficulty}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">{daily.room.description}</p>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="text-[#00FF00] font-semibold">
                            {daily.room.points} pts
                          </span>
                          <span>&rarr;</span>
                          <span className="text-[#00FF00] font-semibold">
                            {daily.room.points * daily.bonus_multiplier} pts today
                          </span>
                        </div>
                      </div>
                    </div>
                    <Button
                      data-testid="daily-challenge-start-btn"
                      onClick={() => navigate(`/room/${daily.room.id}`)}
                      className="w-full bg-[#00FF00] text-black hover:bg-[#00DD00] font-semibold"
                    >
                      {daily.completed_today ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Replay Challenge
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Start Daily Challenge
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Continue Learning */}
            <Card className="bg-[#1A1A1A] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Continue Learning</CardTitle>
                <CardDescription className="text-gray-400">Pick up where you left off</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {paths.slice(0, 2).map((path) => (
                  <div key={path.id} className="border border-gray-800 rounded-lg p-4 hover:border-[#00FF00]/50 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-white mb-1">{path.title}</h3>
                        <p className="text-sm text-gray-400">{path.description}</p>
                      </div>
                      <Badge 
                        className="ml-2" 
                        style={{ backgroundColor: `${path.color || '#00FF00'}20`, color: path.color || '#00FF00', borderColor: path.color || '#00FF00' }}
                      >
                        {path.level}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-gray-400">
                        <span>{path.modules?.length || 0} modules</span>
                        <span>0% complete</span>
                      </div>
                      <Progress value={0} className="h-2" />
                    </div>
                    <Button 
                      onClick={() => navigate('/paths')}
                      className="w-full mt-4 bg-[#00FF00] text-black hover:bg-[#00DD00]"
                    >
                      <Play className="w-4 h-4 mr-2" />
                      Continue Path
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Recommended Rooms */}
            <Card className="bg-[#1A1A1A] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white">Recommended Rooms</CardTitle>
                <CardDescription className="text-gray-400">Start your training here</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {rooms.slice(0, 4).map((room) => (
                  <div 
                    key={room.id}
                    onClick={() => navigate(`/room/${room.id}`)}
                    className="border border-gray-800 rounded-lg p-4 hover:border-[#00BFFF]/50 transition-all cursor-pointer hover:transform hover:scale-[1.02]"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-white">{room.title}</h3>
                          <Badge 
                            variant="outline"
                            className={`text-xs ${difficultyBadgeClass(room.difficulty)}`}
                          >
                            {room.difficulty}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-400 mb-2">{room.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{room.type || 'Challenge'}</span>
                          <span>•</span>
                          <span>{room.timeEstimate || '30 min'}</span>
                          <span>•</span>
                          <span className="text-[#00FF00]">{room.points} pts</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <SandboxCreditsCard />
            <UpgradePrompt context="dashboard" />
            {/* Quick Actions */}
            <Card className="bg-[#1A1A1A] border-gray-800">
              <CardHeader>
                <CardTitle className="text-white text-sm">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  onClick={() => navigate('/paths')}
                  variant="outline" 
                  className="w-full justify-start border-gray-700 text-white hover:bg-gray-800"
                >
                  Browse Learning Paths
                </Button>
                <Button 
                  onClick={() => navigate('/rooms')}
                  variant="outline" 
                  className="w-full justify-start border-gray-700 text-white hover:bg-gray-800"
                >
                  Explore All Rooms
                </Button>
                <Button 
                  onClick={() => navigate('/leaderboard')}
                  variant="outline" 
                  className="w-full justify-start border-gray-700 text-white hover:bg-gray-800"
                >
                  View Leaderboard
                </Button>
                <Button 
                  onClick={() => navigate('/profile')}
                  variant="outline" 
                  className="w-full justify-start border-gray-700 text-white hover:bg-gray-800"
                >
                  My Profile & Badges
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />

      {showAuthModal && (
        <AuthModal 
          isOpen={showAuthModal} 
          onClose={() => setShowAuthModal(false)}
          initialTab="login"
        />
      )}
    </div>
  );
};

export default Dashboard;
